<template>
  <div>
    <settings-section-title id="edit" class="i-mb-4"
      >Edit API Key</settings-section-title
    >
    <breadcrumbs :items="breadcrumbs"></breadcrumbs>

    <div v-if="hasEditPermissions" class="i-mt-12 edit-form">
      <label
        for="key-name"
        class="i-text-subheading-2 i-text-neutral-120 i-mb-2"
        >Name</label
      >

      <b-form-input
        id="key-name"
        v-model="$v.keyName.$model"
        placeholder="Enter a name for the API Key"
        class="i-form-input"
        :class="{ error: hasAnyError($v.keyName) || keyNameExists }"
      ></b-form-input>

      <div class="i-mt-4 i-text-body-3 i-text-error-110">
        <p v-if="hasAnyError($v.keyName) && !$v.keyName.required">
          The API Key name field is required
        </p>
        <p v-if="hasAnyError($v.keyName) && !$v.keyName.maxLength">
          The API Key Name has a maximum length limit of 40 characters
        </p>
        <p v-if="keyNameExists">
          The API Key Name must be unique, another key with this name exists
        </p>
      </div>

      <label
        for="key-description"
        class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
        >Description (optional)</label
      >
      <b-form-textarea
        id="key-description"
        v-model="$v.keyDescription.$model"
        placeholder="Enter a description"
        class="i-form-textarea"
        :class="{ error: hasAnyError($v.keyDescription) }"
      ></b-form-textarea>
      <div class="i-mt-4 i-text-body-3">
        <p
          v-if="hasAnyError($v.keyDescription) && !$v.keyDescription.maxLength"
          class="i-text-error-110"
        >
          The API Key Description field has a maximum length limit of
          <!-- eslint-disable-next-line prettier/prettier -->
          150 characters
        </p>
      </div>

      <div class="d-flex i-mt-12">
        <i-button
          :disabled="$v.$error || $v.$invalid || keyNameExists"
          text="Save"
          size="lg"
          class="i-mr-6"
          @button-click="save()"
        ></i-button>

        <i-button
          text="Cancel"
          size="lg"
          type="neutral"
          subtype="subtle"
          @button-click="cancel()"
        ></i-button>
      </div>
    </div>

    <div v-else class="i-mt-12">
      <p>You must be an administrator to edit this API key</p>
    </div>
  </div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { iButton } from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';

export default {
  components: {
    Breadcrumbs,
    'i-button': iButton,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'API Keys',
          to: { name: 'api-settings-list-keys' },
        },
        {
          text: 'Edit API Key',
          active: true,
        },
      ],
      apiKey: '',
      keyName: '',
      keyDescription: '',
      currentKey: {},
      existingKeyNames: [],
    };
  },
  validations: {
    keyName: {
      required,
      maxLength: maxLength(40),
    },
    keyDescription: {
      maxLength: maxLength(150),
    },
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),

    ...mapState(['currentUser']),

    keyNameExists() {
      return (
        this.existingKeyNames && this.existingKeyNames.includes(this.keyName)
      );
    },
  },
  mounted() {
    this.apiKeyUUID = this.$route.params.api_key;
    this.currentKey = this.currentUser.organisation.api_keys.filter(
      (key) => key.key === this.apiKeyUUID,
    )[0];
    this.keyName = this.currentKey.name;
    this.keyDescription = this.currentKey.description;

    this.existingKeyNames = this.currentUser.organisation.api_keys.reduce(
      (results, apiKeys) => {
        if (apiKeys.key !== this.currentKey.key && apiKeys.name) {
          results.push(apiKeys.name);
        }
        return results;
      },
      [],
    );
  },
  methods: {
    ...mapActions(['editApiKey']),

    ...mapMutations(['updateApiKeyDetail']),

    hasAnyError(validation) {
      return validation.$dirty && validation.$error;
    },

    cancel() {
      this.$router.push({
        name: 'api-settings-list-keys',
      });
    },
    save() {
      const keyData = {
        name: this.keyName,
        description: this.keyDescription,
      };

      this.editApiKey({
        uuid: this.apiKeyUUID,
        keyDetail: keyData,
      })
        .then((response) => {
          this.updateApiKeyDetail({
            key: this.apiKeyUUID,
            ...keyData,
          });
          this.$notify({
            group: 'api',
            type: 'success',
            duration: 6000,
            text: `${this.keyName} has been updated`,
          });
          this.$router.push({
            name: 'api-settings-list-keys',
          });
        })
        .catch(() => {
          this.$notify({
            group: 'api',
            type: 'error',
            duration: 6000,
            text: 'Error updating API Key',
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-form {
  max-width: 500px;
}
</style>
